/**
 * Web Audio Synthesis Helpers
 *
 * Low-level utilities for synthesizing short SFX ("martelados") from scratch
 * using OscillatorNode + GainNode. No audio files needed for the Fase 0
 * set — keeps the bundle small and gives us infinite variations for free.
 *
 * Design notes:
 *  - All helpers take an AudioContext + a destination GainNode, so the caller
 *    decides where the sound lands in the mix graph.
 *  - Every note uses an explicit envelope (attack + release) to avoid clicks
 *    — a critical detail in Web Audio; a raw square wave at amplitude 1
 *    has infinite harmonics that make an audible "tick" on start/stop.
 *  - Scheduling is done in absolute AudioContext time so multiple notes
 *    layered in one SFX stay in phase.
 *
 * Why not <audio> elements: on iOS Safari, a second <audio> element
 * playing concurrently with the TTS one is sometimes silently muted.
 * Synthesized nodes go through the same AudioContext and don't have that
 * limitation. See [playCompletionSound] in the admin editor for the same
 * pattern.
 */

export type SfxWaveType = OscillatorType;

/** Linear ADSR-ish envelope, fixed to a single sustain level.
 *  No need for the AudioContext — the GainParam's automation methods
 *  don't need it and the caller already has the timing context. */
export function applyEnvelope(
  gain: GainNode,
  /** When the note starts (absolute ctx time). */
  startAt: number,
  /** Note duration in seconds. */
  duration: number,
  /** Peak gain during the note. */
  peak: number,
): void {
  // Attack: 0 → peak in 5ms (avoids click).
  // Release: peak → 0 with a short linear tail (avoids click at end).
  const attack = Math.min(0.005, duration * 0.1);
  const release = Math.min(0.04, duration * 0.2);
  const sustainEnd = startAt + duration;
  const releaseEnd = sustainEnd + release;

  const g = gain.gain;
  g.cancelScheduledValues(startAt);
  g.setValueAtTime(0, startAt);
  g.linearRampToValueAtTime(peak, startAt + attack);
  g.setValueAtTime(peak, sustainEnd);
  g.linearRampToValueAtTime(0, releaseEnd);
}

/**
 * Play a single tone (oscillator + envelope). Returns when the note
 * fully decays so the caller can chain notes.
 */
export function playTone(
  ctx: AudioContext,
  dest: GainNode,
  opts: {
    freq: number;
    startAt?: number;
    duration: number;
    type?: SfxWaveType;
    /** Linear peak amplitude 0..1. Default 0.3 (safe). */
    peak?: number;
  },
): number {
  const startAt = opts.startAt ?? ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = opts.type ?? 'sine';
  osc.frequency.setValueAtTime(opts.freq, startAt);
  osc.connect(gain);
  gain.connect(dest);
  applyEnvelope(gain, startAt, opts.duration, opts.peak ?? 0.3);
  osc.start(startAt);
  // Oscillator must be kept alive at least until the release tail ends,
  // otherwise the release is cut short. Stop a hair after release end.
  const release = Math.min(0.04, opts.duration * 0.2);
  osc.stop(startAt + opts.duration + release + 0.01);
  return startAt + opts.duration + release;
}

/**
 * Frequency sweep (glissando) from `fromHz` to `toHz` over `duration`s.
 * Uses exponentialRampToValueAtTime for a perceptually natural glide
 * (linear freq ramps sound "wrong" because we hear pitch logarithmically).
 */
export function playSweep(
  ctx: AudioContext,
  dest: GainNode,
  opts: {
    fromHz: number;
    toHz: number;
    startAt?: number;
    duration: number;
    type?: SfxWaveType;
    peak?: number;
  },
): number {
  const startAt = opts.startAt ?? ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = opts.type ?? 'sine';
  osc.frequency.setValueAtTime(Math.max(1, opts.fromHz), startAt);
  // exponentialRamp requires non-zero start value; we set 1 minimum above.
  osc.frequency.exponentialRampToValueAtTime(
    Math.max(1, opts.toHz),
    startAt + opts.duration,
  );
  osc.connect(gain);
  gain.connect(dest);
  applyEnvelope(gain, startAt, opts.duration, opts.peak ?? 0.3);
  osc.start(startAt);
  const release = Math.min(0.04, opts.duration * 0.2);
  osc.stop(startAt + opts.duration + release + 0.01);
  return startAt + opts.duration + release;
}

/**
 * Quick low-pass filter helper for warmer/muffled tones. Wraps the
 * signal path so callers can layer it onto a tone or a sweep.
 */
export function makeLowpass(
  ctx: AudioContext,
  cutoffHz: number,
  Q = 1,
): BiquadFilterNode {
  const f = ctx.createBiquadFilter();
  f.type = 'lowpass';
  f.frequency.value = cutoffHz;
  f.Q.value = Q;
  return f;
}
